import { Boundaries } from "@planzone/core";
import { ResourceAllocations, resourceAllocations } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceList } from "../../common/list";
import { reduceResource } from "../../common/resource";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  {
    project,
    projectResource,
  }: Boundaries["retrieveProjectResourceAllocations"]["request"],
): State {
  const index = state.findIndex(
    (req) =>
      req.project.uuid === project.uuid &&
      req.projectResource.uuid === projectResource.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          projectResource,
          request: setRequestStateToPending(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          projectResource,
          request: setRequestStateToPending(),
          resourceAllocations,
          isDirty: false,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    project,
    projectResource,
    resourceAllocations,
  }: Boundaries["retrieveProjectResourceAllocations"]["success"],
): State {
  const index = state.findIndex(
    (req) =>
      req.project.uuid === project.uuid &&
      req.projectResource.uuid === projectResource.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          resourceAllocations,
          request: setRequestStateToSuccess(),
          projectResource,
          isDirty: false,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          resourceAllocations,
          request: setRequestStateToSuccess(),
          projectResource,
          isDirty: false,
        },
      ];
}

function handleFailure(
  state: State,
  {
    project,
    projectResource,
  }: Boundaries["retrieveProjectResourceAllocations"]["failure"],
): State {
  const index = state.findIndex(
    (req) =>
      req.project.uuid === project.uuid &&
      req.projectResource.uuid === projectResource.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          projectResource,
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          projectResource,
          request: setRequestStateToFailure(),
          resourceAllocations,
          isDirty: false,
        },
      ];
}

function handleAllocationResources(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    resourceAllocations: {
      ...req.resourceAllocations,
      resource: {
        ...req.resourceAllocations.resource,
        ...reduceResource(req.resourceAllocations.resource, event),
        lists: req.resourceAllocations.resource.lists.map((list) => ({
          ...list,
          ...(reduceList(
            list,
            event,
          ) as ResourceAllocations["resource"]["lists"][number]),
        })),
      },
    },
  }));
}

const reduceState = createStateReducer(
  initialState,
  {
    [events.retrieveProjectResourceAllocationsRequested]: handleRequest,
    [events.retrieveProjectResourceAllocationsSucceeded]: handleSuccess,
    [events.retrieveProjectResourceAllocationsFailed]: handleFailure,
    [events.allocateResourcesSucceeded]: handleAllocationResources,
  },
  propagateEvent,
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
